import { editorialEntities, type EditorialEntity, type SourceIssue } from "./sources-audit";
import { sourcesFor } from "@/data/sources";

/**
 * Conformité éditoriale des textes signés.
 *
 * Complète `sources-audit` (qui vérifie la présence de références) par des
 * contrôles de formulation : présomption d'innocence, culpabilité affirmée
 * sans décision de justice, imputations non attribuées à une source.
 *
 * Objectif : qu'aucune phrase publiée sous une signature nominative ne
 * puisse être lue comme une accusation personnelle de l'auteur.
 */

/** Découpe grossière en phrases (suffisante pour du texte éditorial court). */
export function sentences(text: string): string[] {
  return text
    .split(/(?<=[.!?…])\s+(?=[«"A-ZÀ-ÖØ-Þ])/u)
    .map((s) => s.trim())
    .filter(Boolean);
}

/** Marqueurs qui rattachent une affirmation à une décision ou à une source. */
const ATTRIBUTION_RE =
  /\b(selon|d'après|dapres|pour la (?:cour|justice)|la cour|le tribunal|les? juges?|la (?:chambre|cour) d'appel|le parquet|le procureur|l'enquête|les enquêteurs|l'accusation|la défense|le jugement|l'arrêt|le verdict|condamn|acquitt|relax|reconnu coupable|déclaré coupable|mis en examen|poursuivi|prévenu|accusé|présumé|soupçonn|selon la presse|d'après les? (?:sources?|médias?)|revendiqu|attribué)/i;

/** Culpabilité affirmée à l'indicatif, sans réserve ni attribution. */
const GUILT_RE =
  /\b(?:a (?:assassiné|tué|abattu|commandité|extorqué|détourné|racketté)|est l'(?:assassin|auteur|instigateur|commanditaire)|sont les (?:assassins|auteurs|commanditaires)|est le (?:meurtrier|tueur|chef de la mafia)|a orchestré (?:l'assassinat|le meurtre))(?![\p{L}])/iu;

/** Imputations et rapprochements qui exigent une attribution explicite. */
const IMPUTATION_RE =
  /\b(?:proche de la mafia|lié à la mafia|membre du (?:gang|milieu)|figure du (?:milieu|banditisme)|parrain|réseau mafieux|homme de main|blanchiment|corruption|trafic)(?![\p{L}])/iu;

/** Procédures non définitives : la présomption d'innocence doit être rappelée. */
const ONGOING_RE =
  /\b(?:mis en examen|instruction (?:est )?(?:en cours|ouverte)|information judiciaire|procès (?:à venir|s'ouvrira|doit s'ouvrir)|en attente de jugement|pourvoi en cassation|frappée? d'appel|procédure en cours|interjeté appel)\b/i;

/** Formulations qui satisfont l'obligation de rappel. */
const PRESUMPTION_RE =
  /\b(?:présomption d'innocence|présumée? innocente?|n'a pas été jugée?|aucune décision définitive|pas de condamnation définitive|jusqu'à (?:une )?décision définitive|tant qu'(?:aucune|elle n'est pas))\b/i;

/** Modèle de phrase inséré automatiquement quand une procédure ouverte est détectée. */
export const PRESUMPTION_NOTICE =
  "À ce stade de la procédure, aucune décision définitive n'a été rendue : la présomption d'innocence s'applique à toutes les personnes mises en cause.";

/** Vrai si le texte évoque une procédure non définitive sans rappel suffisant. */
export function needsPresumptionNotice(text: string): boolean {
  return ONGOING_RE.test(text) && !PRESUMPTION_RE.test(text);
}

/**
 * Garantit la mention de présomption d'innocence : si une procédure ouverte
 * est détectée sans rappel, le modèle de phrase est ajouté en fin de texte.
 * Idempotent — un texte déjà conforme est renvoyé inchangé.
 */
export function ensurePresumptionNotice(text: string): string {
  if (!needsPresumptionNotice(text)) return text;
  const trimmed = text.trimEnd();
  return `${trimmed}${trimmed.endsWith(".") ? "" : "."} ${PRESUMPTION_NOTICE}`;
}

export type ComplianceIssue = SourceIssue & { excerpt?: string };

const push = (
  out: ComplianceIssue[],
  e: EditorialEntity,
  level: "error" | "warning",
  reason: string,
  excerpt?: string,
) => out.push({ key: e.key, label: e.label, level, reason, excerpt });

/** Contrôle de conformité d'une rubrique éditoriale. */
export function auditEntityCompliance(e: EditorialEntity): ComplianceIssue[] {
  const out: ComplianceIssue[] = [];
  const parts = sentences(e.text);

  for (const s of parts) {
    // Les questions de FAQ ne sont pas des affirmations.
    if (s.trim().endsWith("?")) continue;
    if (GUILT_RE.test(s) && !ATTRIBUTION_RE.test(s)) {
      push(out, e, "error", "culpabilité affirmée sans décision de justice ni attribution", s);
    }
    if (IMPUTATION_RE.test(s) && !ATTRIBUTION_RE.test(s)) {
      push(out, e, "warning", "imputation sans attribution explicite à une source", s);
    }
  }

  if (ONGOING_RE.test(e.text) && !PRESUMPTION_RE.test(e.text)) {
    push(
      out,
      e,
      "error",
      "procédure non définitive évoquée sans rappel de la présomption d'innocence",
      parts.find((s) => ONGOING_RE.test(s)),
    );
  }

  // Une rubrique judiciaire doit s'appuyer sur au moins une référence datée.
  if (ONGOING_RE.test(e.text) || GUILT_RE.test(e.text)) {
    const refs = sourcesFor(e.key);
    if (!refs.some((r) => r.date)) {
      push(out, e, "warning", "rubrique judiciaire sans référence datée");
    }
  }

  return out;
}

/** Contrôle de conformité de tout le corpus éditorial publié. */
export function auditCompliance(entities = editorialEntities()): ComplianceIssue[] {
  return entities.flatMap(auditEntityCompliance);
}

export function formatCompliance(issues: ComplianceIssue[]): string {
  return issues
    .map(
      (i) =>
        `${i.level === "error" ? "✗" : "!"} ${i.key} — ${i.label} : ${i.reason}` +
        (i.excerpt ? `\n    « ${i.excerpt.slice(0, 220)} »` : ""),
    )
    .join("\n");
}
